import { useState, useEffect } from "react";
import { toast } from "sonner";
import { LogOut, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { FadeUp } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const formatError = (detail) => {
  if (!detail) return "Errore imprevisto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export default function Admin() {
  const { token, login, logout, settings, refreshSettings } = useSite();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (token && settings && !form) {
      setForm({
        stats: settings.stats.map((s) => ({ ...s })),
        whatsapp_url: settings.whatsapp_url,
      });
    }
  }, [token, settings, form]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token);
      toast.success("Accesso effettuato");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await refreshSettings();
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStat = (idx, field, value) => {
    setForm((f) => {
      const stats = f.stats.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
      return { ...f, stats };
    });
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-16" data-testid="admin-login-page">
        <FadeUp className="w-full max-w-md">
          <form
            onSubmit={handleLogin}
            className="border border-[#1A1A1A] bg-[#0d0d0d] p-8 lg:p-10"
            data-testid="admin-login-form"
          >
            <div className="flex flex-col items-center text-center">
              <img src="/assets/iem-emblem.png" alt="IEM" className="h-16 w-16 rounded-full ring-1 ring-gold/25" />
              <h1 className="mt-6 font-serif text-2xl sm:text-3xl text-[#F5F3EE]">Area Riservata</h1>
              <p className="mt-2 text-[11px] uppercase tracking-[0.28em] text-gold">IEM — Admin</p>
            </div>
            <div className="mt-8 space-y-5">
              <div>
                <Label htmlFor="admin-email" className="text-[11px] uppercase tracking-[0.2em] text-[#969696]">
                  Email
                </Label>
                <Input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 border-[#1A1A1A] bg-[#111111] text-[#F5F3EE] focus-visible:ring-gold/50"
                  autoComplete="username"
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-[11px] uppercase tracking-[0.2em] text-[#969696]">
                  Password
                </Label>
                <Input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 border-[#1A1A1A] bg-[#111111] text-[#F5F3EE] focus-visible:ring-gold/50"
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <p data-testid="admin-login-error" className="border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
                  {error}
                </p>
              )}
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={loading}
                className="w-full bg-gold py-3.5 text-[13px] font-bold tracking-[0.2em] text-[#080808] transition-colors duration-300 hover:bg-gold-bright disabled:opacity-50"
              >
                {loading ? "ACCESSO..." : "ACCEDI"}
              </button>
            </div>
          </form>
        </FadeUp>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen max-w-5xl px-6 pb-24 pt-32 lg:pt-40" data-testid="admin-dashboard">
      <FadeUp className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">Area Riservata</p>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-[#F5F3EE]">Pannello di controllo</h1>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="inline-flex items-center gap-2 border border-[#2a2a2a] px-5 py-2.5 text-[12px] font-semibold tracking-[0.18em] text-[#969696] transition-colors hover:border-gold/50 hover:text-gold"
        >
          <LogOut size={14} /> ESCI
        </button>
      </FadeUp>

      {form && (
        <>
          <FadeUp delay={0.1} className="mt-12 border border-[#1A1A1A] bg-[#0d0d0d] p-8">
            <h2 className="font-serif text-xl sm:text-2xl text-[#F5F3EE]" data-testid="admin-stats-heading">
              Numeri in homepage
            </h2>
            <p className="mt-2 text-sm text-[#969696]">
              Modifica i valori mostrati nella sezione numeri. Le modifiche sono visibili immediatamente sul sito.
            </p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {form.stats.map((s, i) => (
                <div key={s.id} className="border border-[#1A1A1A] bg-[#111111] p-5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-gold/80">Valore</Label>
                  <Input
                    data-testid={`admin-stat-number-${s.id}`}
                    value={s.number}
                    onChange={(e) => setStat(i, "number", e.target.value)}
                    className="mt-2 border-[#2a2a2a] bg-[#0b0b0b] font-serif text-2xl text-[#F5F3EE] focus-visible:ring-gold/50"
                  />
                  <Label className="mt-4 block text-[10px] uppercase tracking-[0.2em] text-gold/80">Etichetta</Label>
                  <Input
                    data-testid={`admin-stat-label-${s.id}`}
                    value={s.label}
                    onChange={(e) => setStat(i, "label", e.target.value)}
                    className="mt-2 border-[#2a2a2a] bg-[#0b0b0b] text-sm uppercase tracking-[0.15em] text-[#969696] focus-visible:ring-gold/50"
                  />
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="mt-8 border border-[#1A1A1A] bg-[#0d0d0d] p-8">
            <h2 className="font-serif text-xl sm:text-2xl text-[#F5F3EE]" data-testid="admin-links-heading">
              Link di contatto
            </h2>
            <div className="mt-8">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-gold/80">WhatsApp URL</Label>
                <Input
                  data-testid="admin-whatsapp-input"
                  value={form.whatsapp_url}
                  onChange={(e) => setForm((f) => ({ ...f, whatsapp_url: e.target.value }))}
                  className="mt-2 border-[#2a2a2a] bg-[#111111] text-sm text-[#F5F3EE] focus-visible:ring-gold/50"
                />
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-8">
            <button
              data-testid="admin-save-button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold tracking-[0.18em] text-[#080808] transition-colors duration-300 hover:bg-gold-bright disabled:opacity-50"
            >
              <Save size={15} /> {saving ? "SALVATAGGIO..." : "SALVA MODIFICHE"}
            </button>
          </FadeUp>
        </>
      )}
    </main>
  );
}
